import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "../ui/sheet";
import { Button } from "../ui/button";
import { CircleCheck, Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { trackBuyAgainWidgetInteract } from "@/lib/tracking";
import {
  getDisplayReasons,
  getCancelReasonLabel,
  type CancelSubscriptionReasonId,
} from "./cancel-reasons";
import {
  scheduledMessage,
  scheduledTitle,
} from "./cancel-after-delivery-copy";
import { BeforeYouCancelBody } from "./BeforeYouCancelBody";
import { InvoicePaymentStep } from "./InvoicePaymentStep";
import { PostShipmentRetentionBody } from "./PostShipmentRetentionBody";
import { buildRetentionOfferSequence } from "./retention-offers";
import { getDelayPresets } from "./pause-dates";
import type { ManageSubscriptionSheetProduct } from "./types";
import {
  cancelFlowTrackingProps,
  createCancelAttemptId,
} from "./cancel-attempt-id";

export type CancelStep =
  | "why-bought"
  | "reason"
  | "before-cancel"
  | "confirm"
  | "invoice-payment";

export type CancelAction =
  | "cancel"
  | "switch_to_one_time"
  | "cancel_after_next_delivery";

export interface CancelInvoicePayment {
  invoiceUrl: string;
  amount: number;
  currency: string;
}

export interface CancelSubscriptionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  product: ManageSubscriptionSheetProduct;
  subscriptionOrderCount?: number | null;
  isBeforeSecondShipment?: boolean;
  isFlexibleSubscription?: boolean;
  initialStep?: CancelStep;
  trackingShop?: string | null;
  onSubmit: (data: {
    whyBought: string;
    cancelReason: string;
    cancelReasonOther?: string;
    cancelReasonId: CancelSubscriptionReasonId;
    cancelAction: CancelAction;
    offerDeclined: boolean;
    cancelAttemptId: string;
  }) => void;
  submitPending: boolean;
  submitSuccess: boolean;
  invoicePayment?: CancelInvoicePayment | null;
  onDelay?: () => void;
  onChangeFrequency?: () => void;
  cancelAfterDeliveryEnabled: boolean;
}

export const PILL_FILLED_CLASS =
  "tw:w-full tw:rounded-md tw:bg-[#FAC8CA] tw:border-[#FAC8CA] tw:text-[#523D33] hover:tw:bg-[#FAC8CA]/80 disabled:tw:opacity-40 disabled:tw:cursor-not-allowed tw:font-bold tw:uppercase tw:tracking-wide tw:text-[14px]";

export const PILL_OUTLINE_CLASS =
  "tw:w-full tw:rounded-md tw:border-[#523D33] tw:box-border tw:text-[#523D33] tw:shadow-none hover:tw:bg-[#523D33]/5 tw:bg-clip-border tw:font-bold tw:uppercase tw:tracking-wide tw:text-[14px]";

function useIsDesktop(breakpoint = 768): boolean {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" && window.innerWidth >= breakpoint,
  );
  useEffect(() => {
    const fn = () => setIsDesktop(window.innerWidth >= breakpoint);
    fn();
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, [breakpoint]);
  return isDesktop;
}

export function CancelSubscriptionDialog({
  open,
  onOpenChange,
  product,
  subscriptionOrderCount,
  isBeforeSecondShipment,
  isFlexibleSubscription = false,
  initialStep,
  trackingShop,
  onSubmit,
  submitPending,
  submitSuccess,
  invoicePayment,
  onDelay,
  onChangeFrequency,
  cancelAfterDeliveryEnabled,
}: CancelSubscriptionDialogProps) {
  const productTitle = product.title;
  const [step, setStep] = useState<CancelStep>(initialStep ?? "why-bought");
  const [direction, setDirection] = useState<"forward" | "back">("forward");
  const [whyBoughtText, setWhyBoughtText] = useState("");
  const [cancelReasonId, setCancelReasonId] =
    useState<CancelSubscriptionReasonId | null>(null);
  const [otherReasonText, setOtherReasonText] = useState("");
  const [cancelAction, setCancelAction] = useState<CancelAction>("cancel");
  const cancelAttemptIdRef = useRef("");
  const cancelTrackProps = () =>
    cancelFlowTrackingProps({
      cancelAttemptId: cancelAttemptIdRef.current,
      orderCount: subscriptionOrderCount,
    });
  const wasOpenRef = useRef(false);
  const otherReasonTextareaRef = useRef<HTMLTextAreaElement>(null);

  if (open && !wasOpenRef.current) {
    cancelAttemptIdRef.current = createCancelAttemptId();
  } else if (!open && wasOpenRef.current) {
    cancelAttemptIdRef.current = "";
  }
  wasOpenRef.current = open;

  const isDesktop = useIsDesktop();

  useEffect(() => {
    if (open && initialStep) {
      setStep(initialStep);
      setDirection("forward");
    }
  }, [open, initialStep]);

  useEffect(() => {
    if (!open || !trackingShop) return;
    trackBuyAgainWidgetInteract("subscription_cancel_open", trackingShop, {
      productId: product.id,
      subscription_contract_id: product.shopify_subscription_id ?? undefined,
      ...cancelTrackProps(),
    });
  }, [open, trackingShop, product.id, product.shopify_subscription_id]);

  useEffect(() => {
    if (!open) {
      setStep(initialStep ?? "why-bought");
      setDirection("forward");
      setWhyBoughtText("");
      setCancelReasonId(null);
      setOtherReasonText("");
      setCancelAction("cancel");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on close only
  }, [open]);

  const displayReasons = useMemo(
    () =>
      getDisplayReasons(
        undefined,
        subscriptionOrderCount,
        isFlexibleSubscription,
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute on open only
    [open, subscriptionOrderCount, isFlexibleSubscription],
  );

  const delayAvailable = useMemo(
    () =>
      getDelayPresets(
        product.subscription_next_billing_date,
        product.subscription_last_charge_date,
      ).length > 0,
    [product.subscription_next_billing_date, product.subscription_last_charge_date],
  );
  const changeFrequencyAvailable = onChangeFrequency != null;
  const retentionOffers = useMemo(
    () =>
      cancelReasonId && !isBeforeSecondShipment
        ? buildRetentionOfferSequence(cancelReasonId, {
            delayAvailable,
            changeFrequencyAvailable,
          })
        : [],
    [
      cancelReasonId,
      isBeforeSecondShipment,
      delayAvailable,
      changeFrequencyAvailable,
    ],
  );

  const goTo = (next: CancelStep, dir: "forward" | "back" = "forward") => {
    setDirection(dir);
    setStep(next);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setStep("why-bought");
      setDirection("forward");
      setWhyBoughtText("");
      setCancelReasonId(null);
      setOtherReasonText("");
      setCancelAction("cancel");
    }
    onOpenChange(nextOpen);
  };

  const submitCancellation = (action: CancelAction) => {
    if (cancelReasonId == null) return;
    onSubmit({
      whyBought: whyBoughtText.trim(),
      cancelReason: getCancelReasonLabel(cancelReasonId),
      cancelReasonOther:
        cancelReasonId === "other" ? otherReasonText.trim() : undefined,
      cancelReasonId,
      cancelAction: action,
      offerDeclined: false,
      cancelAttemptId: cancelAttemptIdRef.current,
    });
  };

  // Flexible subs skip retention. Post-shipment reasons with no available offers
  // also skip straight to the confirm.
  const skipRetentionStep =
    isFlexibleSubscription ||
    (!isBeforeSecondShipment &&
      cancelReasonId != null &&
      retentionOffers.length === 0);

  const handleWhyBoughtContinue = () => {
    if (trackingShop) {
      trackBuyAgainWidgetInteract(
        "subscription_cancel_why_bought_continue",
        trackingShop,
        {
          productId: product.id,
          subscription_contract_id:
            product.shopify_subscription_id ?? undefined,
          why_bought_length: whyBoughtText.trim().length,
          ...cancelTrackProps(),
        },
      );
    }
    goTo("reason", "forward");
  };

  // Open the keyboard as soon as "Other" is selected (textarea mounts under it).
  useEffect(() => {
    if (cancelReasonId !== "other") return;
    otherReasonTextareaRef.current?.focus();
  }, [cancelReasonId]);

  const handleReasonSelected = (id: CancelSubscriptionReasonId) => {
    if (cancelReasonId === id) {
      setCancelReasonId(id);
      return;
    }
    setCancelReasonId(id);
    if (trackingShop) {
      trackBuyAgainWidgetInteract(
        "subscription_cancel_reason_selected",
        trackingShop,
        {
          productId: product.id,
          subscription_contract_id:
            product.shopify_subscription_id ?? undefined,
          reason: id,
          ...cancelTrackProps(),
        },
      );
    }
  };

  const handleReasonContinue = () => {
    setCancelAction("cancel");
    if (trackingShop) {
      trackBuyAgainWidgetInteract(
        "subscription_cancel_continue",
        trackingShop,
        {
          productId: product.id,
          subscription_contract_id:
            product.shopify_subscription_id ?? undefined,
          reason: cancelReasonId ?? undefined,
          ...cancelTrackProps(),
        },
      );
    }
    goTo(skipRetentionStep ? "confirm" : "before-cancel", "forward");
  };

  const handleConfirmBack = () => {
    goTo(skipRetentionStep ? "reason" : "before-cancel", "back");
  };

  const confirmPending = submitPending;

  const handleConfirm = () => {
    submitCancellation(cancelAction);
  };

  const continueWhyBoughtDisabled = whyBoughtText.trim().length === 0;
  const continueCancelReasonDisabled =
    cancelReasonId == null ||
    (cancelReasonId === "other" && otherReasonText.trim().length === 0);

  // Which container should be visible right now.
  const anySuccess = submitSuccess;
  const inFlowStep =
    step === "why-bought" ||
    step === "reason" ||
    step === "before-cancel";
  const flowSheetOpen = open && !anySuccess && inFlowStep && !isDesktop;
  const flowDialogOpen = open && !anySuccess && inFlowStep && isDesktop;
  const confirmOpen = open && !anySuccess && step === "confirm";
  const invoicePaymentOpen = open && step === "invoice-payment";
  const successOpen =
    open && anySuccess && step !== "invoice-payment" && invoicePayment == null;

  const paidAmount =
    product.subscription_unit_price != null &&
    Number.isFinite(product.subscription_unit_price)
      ? product.subscription_unit_price
      : null;
  const oneTimeAmount =
    product.subscription_one_time_price != null &&
    Number.isFinite(product.subscription_one_time_price)
      ? product.subscription_one_time_price
      : null;
  const estimatedDiff =
    paidAmount != null && oneTimeAmount != null
      ? Math.max(0, oneTimeAmount - paidAmount)
      : 0;
  const invoiceAmount = invoicePayment?.amount ?? estimatedDiff;
  const invoiceCurrency = invoicePayment?.currency ?? "USD";

  const heading =
    step === "why-bought"
      ? `Remind us, why did you buy ${productTitle} in the first place?`
      : step === "reason"
        ? "Tell us why you wish to cancel"
        : null;

  const body =
    step === "before-cancel" ? (
      isBeforeSecondShipment ? (
        <BeforeYouCancelBody
          product={product}
          submitPending={submitPending}
          onSwitchToOneTime={() => {
            setCancelAction("switch_to_one_time");
            submitCancellation("switch_to_one_time");
          }}
          onKeepSubscription={() => handleOpenChange(false)}
          nextBillingDate={product.subscription_next_billing_date}
          onCancelAfterNextDelivery={
            cancelAfterDeliveryEnabled
              ? () => {
                  // Submits directly (no confirm step): the fee step already
                  // states exactly what happens, and the success dialog confirms.
                  setCancelAction("cancel_after_next_delivery");
                  submitCancellation("cancel_after_next_delivery");
                }
              : undefined
          }
        />
      ) : (
        <PostShipmentRetentionBody
          offers={retentionOffers}
          onDelay={() => onDelay?.()}
          onChangeFrequency={onChangeFrequency}
          onExhausted={() => { setCancelAction("cancel"); goTo("confirm", "forward"); }}
        />
      )
    ) : step === "why-bought" ? (
      <textarea
        value={whyBoughtText}
        onInput={(e) =>
          setWhyBoughtText((e.target as HTMLTextAreaElement).value)
        }
        placeholder="Write here..."
        rows={4}
        className="tw:w-full tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:px-3 tw:py-2.5 tw:text-base tw:resize-none tw:outline-none focus:tw:border-[#523D33] tw:transition-colors"
        style={{ color: "var(--so-text, #523D33)" }}
      />
    ) : (
      <fieldset className="tw:flex tw:flex-col tw:gap-3 tw:border-0 tw:p-0 tw:m-0 tw:min-w-0">
        {displayReasons.map((opt) => {
          const inputId = `cancel-reason-${opt.id}`;
          const selected = cancelReasonId === opt.id;
          return (
            <div key={opt.id}>
              <label
                htmlFor={inputId}
                className={cn(
                  "tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:border tw:p-4 tw:text-sm tw:transition-colors",
                  selected
                    ? "tw:border-[#523D33] tw:bg-[#FDEDEE]/50"
                    : "tw:border-gray-200 tw:bg-white hover:tw:bg-gray-50/80",
                )}
              >
                <input
                  id={inputId}
                  type="radio"
                  name="cancel-subscription-reason"
                  value={opt.id}
                  checked={selected}
                  onChange={() => handleReasonSelected(opt.id)}
                  className="tw:size-4.5 tw:shrink-0 tw:accent-[#523D33] tw:cursor-pointer"
                />
                <span
                  className="tw:flex-1 tw:leading-snug"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  {opt.label}
                </span>
              </label>
              {opt.id === "other" && selected && (
                <textarea
                  ref={otherReasonTextareaRef}
                  value={otherReasonText}
                  onInput={(e) =>
                    setOtherReasonText((e.target as HTMLTextAreaElement).value)
                  }
                  placeholder="Please tell us more..."
                  rows={3}
                  className="tw:mt-2 tw:w-full tw:rounded-lg tw:border tw:border-gray-200 tw:bg-white tw:px-3 tw:py-2.5 tw:text-base tw:resize-none tw:outline-none focus:tw:border-[#523D33] tw:transition-colors"
                  style={{ color: "var(--so-text, #523D33)" }}
                />
              )}
            </div>
          );
        })}
      </fieldset>
    );

  const hasOwnFooter = step === "before-cancel";
  const footerButtons = hasOwnFooter ? null : step === "why-bought" ? (
      <>
        <Button
          type="button"
          disabled={continueWhyBoughtDisabled}
          onClick={handleWhyBoughtContinue}
          className={PILL_FILLED_CLASS}
        >
          Continue
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => handleOpenChange(false)}
          className={PILL_OUTLINE_CLASS}
        >
          Back
        </Button>
      </>
    ) : (
      <>
        <Button
          type="button"
          disabled={continueCancelReasonDisabled}
          onClick={handleReasonContinue}
          className={PILL_FILLED_CLASS}
        >
          Continue
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={() => goTo("why-bought", "back")}
          className={PILL_OUTLINE_CLASS}
        >
          Back
        </Button>
      </>
    );

  const stepAnimClass =
    direction === "forward"
      ? "tw:animate-in tw:fade-in tw:slide-in-from-right-3 tw:duration-200"
      : "tw:animate-in tw:fade-in tw:slide-in-from-left-3 tw:duration-200";

  const innerBody: ComponentChildren = (
    <div>
      <div
        key={step}
        className={cn("tw:space-y-4 tw:py-4 tw:px-4", stepAnimClass)}
      >
        {heading != null && (
          <p className="tw:text-base tw:text-center tw:px-2 max-h-[25ch]">
            {heading}
          </p>
        )}
        {body}
      </div>
    </div>
  );

  const scheduledDate = product.subscription_next_billing_date;
  const isScheduledSuccess =
    cancelAction === "cancel_after_next_delivery" && !!scheduledDate;
  const successTitle = isScheduledSuccess
    ? scheduledTitle(estimatedDiff)
    : "Subscription cancelled";
  const successMessage = isScheduledSuccess
    ? scheduledMessage(scheduledDate)
    : "Your subscription has been cancelled.";

  return (
    <>
      {/* Flow: mobile sheet */}
      <Sheet open={flowSheetOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side="bottom"
          showCloseButton={false}
          onOpenChange={handleOpenChange}
          overlayClassName="tw:z-70"
          className="tw:h-auto tw:min-h-135 tw:max-h-[90dvh] tw:flex tw:flex-col tw:gap-0 tw:rounded-t-2xl tw:overflow-hidden tw:z-71 tw:data-[side=bottom]:z-71"
        >
          <SheetHeader className="tw:shrink-0 tw:flex tw:flex-row tw:items-center tw:justify-between tw:pr-2 tw:pb-2">
            <SheetTitle className="tw:font-semibold tw:text-base tw:text-[#523D33]">
              Cancel
            </SheetTitle>
            <SheetClose
              data-slot="sheet-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </SheetClose>
          </SheetHeader>

          {innerBody}

          {footerButtons && (
            <SheetFooter className="tw:shrink-0 tw:bg-background tw:flex-col tw:gap-4 tw:mt-0">
              {footerButtons}
            </SheetFooter>
          )}
        </SheetContent>
      </Sheet>

      {/* Flow: desktop dialog */}
      <Dialog open={flowDialogOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          showCloseButton={false}
          onOpenChange={handleOpenChange}
          overlayClassName="tw:z-70"
          className="tw:z-71 tw:p-4 tw:min-h-135 tw:max-h-[85dvh] tw:flex tw:flex-col"
        >
          <DialogHeader className="tw:shrink-0">
            <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
              <DialogTitle className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left tw:font-semibold tw:text-base tw:text-[#523D33]">
                Cancel
              </DialogTitle>
              <DialogClose
                data-slot="dialog-close"
                render={
                  <Button
                    variant="ghost"
                    className="tw:p-0 tw:m-0 tw:shrink-0"
                    size="icon-sm"
                    aria-label="Close"
                  />
                }
              >
                <X />
              </DialogClose>
            </div>
          </DialogHeader>

          {innerBody}

          {footerButtons && (
            <DialogFooter className="tw:shrink-0 tw:flex-col tw:sm:flex-col tw:gap-4 tw:mt-2 tw:pt-2">
              {footerButtons}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      {/* Confirm: separate Dialog on any viewport */}
      <Dialog open={confirmOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          showCloseButton={false}
          onOpenChange={handleOpenChange}
          overlayClassName="tw:z-70"
          className="tw:z-71 tw:p-4 tw:max-h-[85dvh] tw:flex tw:flex-col"
        >
          <DialogHeader>
            <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
              <DialogTitle
                className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left tw:font-semibold tw:text-base"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                {cancelAction === "switch_to_one_time"
                  ? "Switch to one-time"
                  : "Confirm cancellation"}
              </DialogTitle>
              <DialogClose
                data-slot="dialog-close"
                render={
                  <Button
                    variant="ghost"
                    className="tw:p-0 tw:m-0 tw:shrink-0"
                    size="icon-sm"
                    aria-label="Close"
                  />
                }
              >
                <X />
              </DialogClose>
            </div>
          </DialogHeader>

          <div className="tw:flex-1 tw:overflow-y-auto tw:min-h-0 tw:space-y-4 tw:py-2 tw:animate-in tw:fade-in tw:zoom-in-95 tw:duration-200">
            <div className="tw:space-y-3">
              <div className="tw:rounded-lg tw:border tw:border-gray-200 tw:bg-gray-50/50 tw:px-3 tw:py-2.5">
                <p
                  className="tw:text-xs tw:font-medium tw:mb-1"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  Why you bought
                </p>
                <p
                  className="tw:text-sm tw:m-0"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  {whyBoughtText.trim()}
                </p>
              </div>
              <div className="tw:rounded-lg tw:border tw:border-gray-200 tw:bg-gray-50/50 tw:px-3 tw:py-2.5">
                <p
                  className="tw:text-xs tw:font-medium tw:mb-1"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  Reason for cancelling
                </p>
                <p
                  className="tw:text-sm tw:m-0"
                  style={{ color: "var(--so-text, #523D33)" }}
                >
                  {cancelReasonId ? getCancelReasonLabel(cancelReasonId) : ""}
                </p>
                {cancelReasonId === "other" && otherReasonText.trim() && (
                  <p className="tw:text-sm tw:mt-1 tw:m-0 tw:text-gray-500">
                    {otherReasonText.trim()}
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-2 tw:pt-2">
            <Button
              type="button"
              disabled={confirmPending}
              onClick={handleConfirm}
              className={PILL_FILLED_CLASS}
            >
              {confirmPending ? (
                <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
              ) : cancelAction === "switch_to_one_time" ? (
                "Continue to payment"
              ) : (
                "Confirm cancellation"
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={confirmPending}
              onClick={handleConfirmBack}
              className={PILL_OUTLINE_CLASS}
            >
              Back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice payment: separate Dialog */}
      <Dialog open={invoicePaymentOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          showCloseButton={false}
          onOpenChange={handleOpenChange}
          overlayClassName="tw:z-70"
          className="tw:z-71 tw:p-4 tw:max-h-[85dvh] tw:flex tw:flex-col"
        >
          <DialogHeader>
            <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
              <DialogTitle
                className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left tw:font-semibold tw:text-base"
                style={{ color: "var(--so-text, #523D33)" }}
              >
                Confirm Cancellation
              </DialogTitle>
              <DialogClose
                data-slot="dialog-close"
                render={
                  <Button
                    variant="ghost"
                    className="tw:p-0 tw:m-0 tw:shrink-0"
                    size="icon-sm"
                    aria-label="Close"
                  />
                }
              >
                <X />
              </DialogClose>
            </div>
          </DialogHeader>
          <InvoicePaymentStep
            amount={invoiceAmount}
            currency={invoiceCurrency}
            pending={submitPending}
            onPay={() => {
              if (invoicePayment?.invoiceUrl) {
                window.location.assign(invoicePayment.invoiceUrl);
                return;
              }
              submitCancellation("switch_to_one_time");
            }}
            onClose={() => handleOpenChange(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Success: separate Dialog */}
      <Dialog open={successOpen} onOpenChange={handleOpenChange}>
        <DialogContent
          showCloseButton={false}
          onOpenChange={handleOpenChange}
          overlayClassName="tw:z-70"
          className="tw:z-71 tw:p-4 tw:max-h-[85dvh] tw:flex tw:flex-col"
        >
          <div className="tw:flex tw:flex-col tw:items-center tw:justify-center tw:py-8 tw:px-4 tw:text-center tw:animate-in tw:fade-in tw:zoom-in-95 tw:duration-300">
            <div className="tw:rounded-full tw:bg-[#FDEDEE] tw:p-3 tw:mb-4">
              <CircleCheck
                className="tw:size-10 tw:text-[#523D33]"
                strokeWidth={1.5}
              />
            </div>
            <p
              className="tw:text-lg tw:font-semibold tw:m-0 tw:mb-2"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              {successTitle}
            </p>
            <p className="tw:text-sm tw:m-0 tw:text-gray-500 tw:max-w-65">
              {successMessage}
            </p>
          </div>
          <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-2 tw:pt-2">
            <Button
              type="button"
              onClick={() => handleOpenChange(false)}
              className={PILL_FILLED_CLASS}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
